<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useHead } from '@unhead/vue'
import LoginCard from '@/components/ui/LoginCard.vue'
import QuotePanel from '@/components/ui/QuotePanel.vue'
import AccountLayout from '@/components/layout/AccountLayout.vue'
import { useAuth } from '@/composables/useAuth'
import SubscriptionChoicesRecap from '@/components/ui/SubscriptionChoicesRecap.vue'
import SubscriptionStartCard from '@/components/ui/SubscriptionStartCard.vue'
import type { ProductKey } from '@/router/products'

const route = useRoute()
const { t } = useI18n()
const { isAuthenticated } = useAuth()

// `mounted` évite un mismatch d'hydratation (page pré-rendue SSG, session
// inconnue côté serveur). Connecté → on rend le tunnel dans le chrome de
// l'espace perso (sidebar) au lieu du site public.
const mounted = ref(false)
onMounted(() => { mounted.value = true })
const connected = computed(() => mounted.value && isAuthenticated.value)

const productKey = computed<ProductKey | undefined>(() => route.meta.productKey)
const productLabel = computed(() =>
  productKey.value ? t(`subscription.products.items.${productKey.value}`) : '',
)

useHead({
  title: computed(() => `${productLabel.value} — ${t('seo.title')}`),
})
</script>

<template>
  <!-- Adhérent connecté : tunnel rendu dans le chrome de l'espace perso. -->
  <AccountLayout v-if="connected" active="subscribe">
    <div class="product-page__grid product-page__grid--account">
      <aside class="product-page__col-login">
        <QuotePanel />
        <SubscriptionChoicesRecap
          class="product-page__recap"
          :product-key="productKey"
          :product-label="productLabel"
        />
      </aside>
      <div class="product-page__col-journey">
        <SubscriptionStartCard :product-key="productKey" :product-label="productLabel" />
      </div>
    </div>
  </AccountLayout>

  <!-- Visiteur non connecté : site public. -->
  <main v-else id="main">
    <section class="product-page section">
      <div class="container">
        <div class="product-page__grid">
          <aside class="product-page__col-login">
            <QuotePanel />
            <SubscriptionChoicesRecap
              class="product-page__recap"
              :product-key="productKey"
              :product-label="productLabel"
            />
            <LoginCard collapsible />
          </aside>
          <div class="product-page__col-journey">
            <SubscriptionStartCard :product-key="productKey" :product-label="productLabel" />
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<style scoped>
.product-page__grid {
  display: grid;
  gap: var(--space-8);
  grid-template-columns: 1fr;
}
@media (min-width: 56rem) {
  .product-page__grid {
    grid-template-columns: 17rem minmax(0, 1fr);
    align-items: start;
  }
}

.product-page__col-login,
.product-page__col-journey {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.product-page__col-login { gap: var(--space-3); }

/* Mobile : le parcours d'adhésion (colonne de droite, le plus important)
   passe au-dessus de l'aside « Vos choix » + connexion. */
.product-page__col-journey { order: -1; }

/* Mobile: the choices recap duplicates what the journey already shows,
   so it is hidden to keep the screen focused on the current step. */
.product-page__recap { display: none; }

@media (min-width: 56rem) {
  /* Desktop : on rétablit l'ordre du DOM (aside à gauche, parcours à droite). */
  .product-page__col-journey { order: 0; }
  .product-page__recap { display: flex; }
  .product-page__col-login {
    position: sticky;
    top: calc(var(--topbar-h) + 4.5rem + var(--space-4));
  }
}
</style>
